import Icon from '@/components/Icon';
import { useNotificationStore } from '@/stores/notifications';
import React, { ReactNode, useCallback, useState, MouseEvent } from 'react';
import { Tr, Td, Text, useDisclosure, Flex, Box, Tooltip, Spinner } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { AssignQuizToUsersRequest } from '../../types';
import { DistributeQuizModal } from '@/features/Quiz/components/DistributeQuizModal';
import { shareAdminQuizV2 } from '@/features/Quiz/api/getQuizzes';
import { format } from 'date-fns';
import { QuizSharedByAdminV2Row } from '@/features/Quiz/types';
import InstitutionsModal from '@/features/Playlists/components/InstitutionsModal';
import { InstitutionModalTypes } from '@/features/Playlists';
import { useAdminQuizV2Scorm } from '@/api/scormApi';
import { useAdminQuizV2Cc } from '@/api/ccApi';

type Props = {
    row: QuizSharedByAdminV2Row;
};

const TextEl = ({ children }: { children: ReactNode }) => (
    <Text
        color="black"
        fontSize={'14px'}
        lineHeight={'21px'}
        fontWeight={'500'}
        textAlign="center"
        display="inline-block"
        p="0 10px"
        w={'auto'}>
        {children}
    </Text>
);

export const SharedQuizItemV2 = ({ row }: Props) => {
    const { addNotification } = useNotificationStore();
    const { isOpen: isInstitutionsOpen, onOpen: onInstitutionsOpen, onClose: onInstitutionsClose } = useDisclosure();
    const [institutionModalType, setInstitutionModalType] = useState<InstitutionModalTypes>(null);

    const [error, setError] = useState('');
    const [openDistributeQuiz, setOpenDistributeQuiz] = useState(false);

    const { isLoading: isScormLoading, handleScormDownload } = useAdminQuizV2Scorm({
        quizUuid: row.quizUuid,
        playlistTitle: row.quizTitle ?? '',
    });

    const { isLoading: isCCLoading, handleCCDownload } = useAdminQuizV2Cc({
        quizUuid: row.quizUuid,
        title: row.quizTitle ?? '',
    });

    const handleOpenInstitutionsScorm = () => {
        setInstitutionModalType('scorm');
        onInstitutionsOpen();
    };

    const handleOpenInstitutionsCc = () => {
        setInstitutionModalType('cc');
        onInstitutionsOpen();
    };

    const handleDistributeQuizClose = useCallback(() => {
        setOpenDistributeQuiz(false);
        setError('');
    }, []);

    const handleDistributeQuizOpen = useCallback((e: MouseEvent) => {
        e.preventDefault();
        setOpenDistributeQuiz(true);
    }, []);

    const handleDistributeQuizSubmit = useCallback(
        async (data: AssignQuizToUsersRequest) => {
            try {
                const res = await shareAdminQuizV2(String(row.quizUuid), { emails: data.emails });
                const notFound = (res as { notFound?: string[] }).notFound ?? [];
                const notAccess = (res as { notAccess?: string[] }).notAccess ?? [];
                const shared = (res as { sharedQuizzes?: { sharedWithUserEmail?: string | null }[] }).sharedQuizzes ?? [];
                const already = (res as { alreadySharedQuizzes?: { sharedWithUserEmail?: string | null }[] })
                    .alreadySharedQuizzes ?? [];

                if (notFound.length) {
                    addNotification({
                        type: 'error',
                        title: `Not found: ${notFound.join(', ')}`,
                    });
                }
                if (notAccess.length) {
                    addNotification({
                        type: 'error',
                        title: `Not access: ${notAccess.join(', ')}`,
                    });
                }
                if (already.length) {
                    const emails = already.map((r) => r.sharedWithUserEmail).filter(Boolean);
                    if (emails.length) {
                        addNotification({
                            type: 'error',
                            title: `Already shared: ${emails.join(', ')}`,
                        });
                    }
                }
                if (shared.length) {
                    const emails = shared.map((r) => r.sharedWithUserEmail).filter(Boolean);
                    if (emails.length) {
                        addNotification({
                            type: 'success',
                            title: `Shared: ${emails.join(', ')}`,
                        });
                    }
                }
                setError('');
            } catch (e: unknown) {
                const message = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : '';
                setError(message);
                throw e;
            }
        },
        [addNotification, row.quizUuid]
    );

    const sharedWithFallback = row.sharedWith ? `User #${row.sharedWith}` : '-';
    const sharedWithLabel = row.sharedWithUser
        ? `${row.sharedWithUser.firstName ?? ''} ${row.sharedWithUser.lastName ?? ''} | ${row.sharedWithUser.email ?? ''}`.trim()
        : sharedWithFallback;

    return (
        <>
            <InstitutionsModal
                isOpen={isInstitutionsOpen}
                onClose={onInstitutionsClose}
                handleScormDownload={handleScormDownload}
                handleCCDownload={handleCCDownload}
                isLoading={isScormLoading || isCCLoading}
                type={institutionModalType}
            />
            <DistributeQuizModal
                isOpen={openDistributeQuiz}
                onClose={handleDistributeQuizClose}
                onSubmit={handleDistributeQuizSubmit}
                quiz={row}
                errorMessage={error}
                sharedList={null}
            />
            <Tr>
                <Td w={'45%'} py={'10px'}>
                    <Flex as={Link} to={`/quiz/admin-quiz-v2/view/${row.quizUuid}`} py={'10px'}>
                        <Text color={'black'}>{row.quizTitle}</Text>
                    </Flex>
                </Td>
                <Td w={'45%'} py={'10px'}>
                    <Box color={'black'} lineHeight={'21px'}>
                        {sharedWithLabel}
                    </Box>
                </Td>
                <Td w={'10%'}>
                    <Text color={'black'} lineHeight={'21px'}>
                        {row.createdAt ? format(new Date(row.createdAt), 'MM/dd/yyyy') : '-'}
                    </Text>
                </Td>
                <Td textAlign="center">
                    <TextEl>{row.noOfQuestions ? `${row.noOfQuestions} Questions` : '-'}</TextEl>
                </Td>
                <Td w={'15%'} textAlign="center">
                    <TextEl>{row.endTime ? format(new Date(row.endTime), 'MM/dd/yyyy') : '-'}</TextEl>
                </Td>
                <Td w="auto">
                    <Flex columnGap={'10px'} align="center" justifyContent={'start'}>
                        <Box
                            variant="playlistSmBtn"
                            borderRadius={'16px'}
                            height={'28px'}
                            onClick={handleDistributeQuizOpen}>
                            <Tooltip hasArrow label={'Share quiz'} aria-label={'Share quiz'}>
                                <span>
                                    <Icon highlightOnHover icon="share-from-square" />
                                </span>
                            </Tooltip>
                        </Box>
                        <Box onClick={handleOpenInstitutionsScorm}>
                            <Tooltip hasArrow label={'Download SCORM'} aria-label={'Download SCORM'}>
                                {isScormLoading ? (
                                    <Spinner size={'xs'} />
                                ) : (
                                    <span>
                                        <Icon highlightOnHover icon="file-zipper" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>
                        <Box onClick={handleOpenInstitutionsCc}>
                            <Tooltip hasArrow label={'Download CC'} aria-label={'Download CC'}>
                                {isCCLoading ? (
                                    <Spinner size={'xs'} />
                                ) : (
                                    <span>
                                        <Icon fontSize={'20px'} highlightOnHover icon="closed-captioning" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>
                    </Flex>
                </Td>
            </Tr>
        </>
    );
};
